import { useEffect, useRef, useState } from 'react'
import { styled } from '@mui/system'
import parseHtmlString from 'html-react-parser'
import { intl } from '../../../../i18n'
import { CommentedNews } from '../../../../store/news'
import { TextColor, FontSize } from '../../../../styles/commonStyles'
import { colorPalette } from '../../../style/colorPalette'
import taskIcon from '../../../../assets/icons/global/task.svg'

const TRUNCATED_MESSAGE_MAX_HEIGHT = 80

const Message = styled('div')({
  color: TextColor.BLACK,
  fontSize: FontSize.MEDIUM,
  lineHeight: '25px',
})

const TruncatedMessageContent = styled(Message)({
  borderLeft: '0.25em solid #d0d7de',
  paddingLeft: '10px',
  margin: '5px 0',
})

const MessageBody = styled(Message)({
  minWidth: 0,
})

const ParentDeliverableName = styled('span')({
  color: colorPalette.monotone[4],
  letterSpacing: '0.1em',
})

const TaskIcon = styled('img')({
  width: '14px',
  height: '14px',
  marginRight: '4px',
  verticalAlign: 'middle',
})

const WbsItemLink = styled('a')({
  color: TextColor.MONOTONE,
})

const TruncatedContentInner = styled('div', {
  shouldForwardProp: prop => prop !== 'isOverflowing',
})<{ isOverflowing: boolean }>(({ isOverflowing }) =>
  isOverflowing
    ? { maxHeight: `${TRUNCATED_MESSAGE_MAX_HEIGHT}px`, overflow: 'hidden' }
    : {}
)

type CommentedNewsBodyProps = {
  news: CommentedNews
  onWbsItemClick: (code: string) => void
}

export const CommentedNewsBody = ({
  news,
  onWbsItemClick,
}: CommentedNewsBodyProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    if (!ref.current) return
    const observer = new ResizeObserver(() => {
      if (ref.current && ref.current.offsetWidth > 0) {
        setIsOverflowing(
          ref.current.scrollHeight > TRUNCATED_MESSAGE_MAX_HEIGHT
        )
      }
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [news.commentBody])

  if (!news.commentBody) return null

  return (
    <>
      <MessageBody>
        {news.parentDeliverableName && (
          <>
            <ParentDeliverableName>
              /{news.parentDeliverableName}/
            </ParentDeliverableName>
            <br />
          </>
        )}
        {news.parentDeliverableName && <TaskIcon src={taskIcon} alt="" />}
        {news.wbsItemCode ? (
          <WbsItemLink
            href="#"
            onClick={e => {
              e.preventDefault()
              onWbsItemClick(news.wbsItemCode!)
            }}
          >
            {news.wbsItemDisplayName}
          </WbsItemLink>
        ) : (
          news.wbsItemDisplayName && <span>{news.wbsItemDisplayName}</span>
        )}
      </MessageBody>
      <TruncatedMessageContent>
        <TruncatedContentInner ref={ref} isOverflowing={isOverflowing}>
          {parseHtmlString(news.commentBody)}
        </TruncatedContentInner>
        {isOverflowing && <span>...</span>}
      </TruncatedMessageContent>
      <Message>
        {intl.formatMessage({ id: 'information.commented.body' })}
      </Message>
    </>
  )
}
